import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Lightbulb, X, ArrowRight } from 'lucide-react';
import { trackIntelClick } from './IntelligenceBar';
import { useT } from '../i18n';

/* InsightPopup — level 2 of the intelligence system. A discreet Intercom-style
   card that surfaces ONE high-value insight per visit (max once every 10 min,
   never the same item twice). Auto-dismisses after 18s. Never spammy. */

const SEEN_KEY = 'kx-insights-seen';
const LAST_KEY = 'kx-insight-last';
const COOLDOWN_MS = 10 * 60 * 1000;
const SHOW_AFTER_MS = 40 * 1000;

export default function InsightPopup() {
  const [item, setItem] = useState(null);
  const { lang } = useT();
  const es = lang === 'es';

  useEffect(() => {
    const last = Number(localStorage.getItem(LAST_KEY) || 0);
    if (Date.now() - last < COOLDOWN_MS) return undefined;

    const timer = setTimeout(async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intelligence-feed`);
        if (!r.ok) return;
        const d = await r.json();
        const seen = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
        const next = (d.items || []).find((i) => !seen.includes(i.id));
        if (!next) return;
        localStorage.setItem(SEEN_KEY, JSON.stringify([...seen, next.id].slice(-40)));
        localStorage.setItem(LAST_KEY, String(Date.now()));
        setItem(next);
        setTimeout(() => setItem(null), 18000);
      } catch {
        /* silent */
      }
    }, SHOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!item) return null;
  const title = (es && item.title_es) || item.title;
  const external = /^https?:\/\//.test(item.url);
  const onGo = () => { trackIntelClick(item.id); setItem(null); };

  const body = (
    <>
      <p className="text-[13.5px] font-semibold text-neutral-900 leading-snug pr-5">{title}</p>
      {item.short_text && <p className="mt-1 text-[12px] text-neutral-500 leading-relaxed">{item.short_text}</p>}
      <span className="mt-2.5 inline-flex items-center gap-1 text-[12.5px] font-bold text-[#F77F11]">
        {item.cta || (es ? 'Leer' : 'Read')} <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </>
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 40, y: 10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        exit={{ opacity: 0, x: 40 }}
        transition={{ type: 'spring', stiffness: 260, damping: 24 }}
        className="fixed right-4 sm:right-6 top-32 z-[60] w-[300px] rounded-2xl bg-white border border-neutral-200 shadow-[0_25px_70px_-25px_rgba(0,0,0,0.35)] p-4"
        data-testid="insight-popup"
      >
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex w-6 h-6 rounded-lg bg-orange-50 items-center justify-center">
            <Lightbulb className="w-3.5 h-3.5 text-[#F77F11]" />
          </span>
          <span className="text-[9.5px] tracking-[0.2em] uppercase font-bold text-neutral-400">
            {es ? 'Nuevo insight' : 'New insight'}
          </span>
          <button
            onClick={() => setItem(null)}
            aria-label="Dismiss"
            className="ml-auto text-neutral-400 hover:text-neutral-700 transition-colors"
            data-testid="insight-popup-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {external ? (
          <a href={item.url} target="_blank" rel="noopener noreferrer" onClick={onGo} className="block" data-testid="insight-popup-link">{body}</a>
        ) : (
          <Link to={item.url} onClick={onGo} className="block" data-testid="insight-popup-link">{body}</Link>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
